import { Icon } from '@/tiptap/ui/Icon'
import { Toolbar } from '@/tiptap/ui/Toolbar'
import { useTextmenuCommands } from './hooks/useTextmenuCommands'
import { useTextmenuStates } from './hooks/useTextmenuStates'
import { BubbleMenu, Editor } from '@tiptap/react'
import { memo } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { useTextmenuContentTypes } from './hooks/useTextmenuContentTypes'
import { ContentTypePicker } from './components/ContentTypePicker'

// We memorize the button so each button is not rerendered
// on every editor state change
const MemoButton = memo(Toolbar.Button)
const MemoContentTypePicker = memo(ContentTypePicker)

export type TextMenuProps = {
  editor: Editor
}

export const TextMenu = ({ editor, enableBold }: TextMenuProps) => {
  const commands = useTextmenuCommands(editor)
  const states = useTextmenuStates(editor)
  const blockOptions = useTextmenuContentTypes(editor)

  return (
    <BubbleMenu
      tippyOptions={{
        popperOptions: {
          placement: 'top-start',
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 8,
              },
            },
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['bottom-start', 'top-end', 'bottom-end'],
              },
            },
          ],
        },
        maxWidth: 'calc(100vw - 16px)',
      }}
      editor={editor}
      pluginKey="textMenu"
      shouldShow={states.shouldShow}
      updateDelay={100}
    >
      <Toolbar.Wrapper>
        <MemoContentTypePicker options={blockOptions} />
        <Toolbar.Divider />
        {!!enableBold && <MemoButton tooltip="Bold" tooltipShortcut={['Mod', 'B']} onClick={commands.onBold} active={states.isBold}>
          <Icon name="Bold" />
        </MemoButton>}
        <MemoButton
          tooltip="Italic"
          tooltipShortcut={['Mod', 'I']}
          onClick={commands.onItalic}
          active={states.isItalic}
        >
          <Icon name="Italic" />
        </MemoButton>
        <MemoButton
          tooltip="Underline"
          tooltipShortcut={['Mod', 'U']}
          onClick={commands.onUnderline}
          active={states.isUnderline}
        >
          <Icon name="Underline" />
        </MemoButton>
        <MemoButton
          tooltip="Strikehrough"
          tooltipShortcut={['Mod', 'Shift', 'S']}
          onClick={commands.onStrike}
          active={states.isStrike}
        >
          <Icon name="Strikethrough" />
        </MemoButton>
        <Toolbar.Divider />
        <MemoButton
          tooltip="Align left"
          tooltipShortcut={['Shift', 'Mod', 'L']}
          onClick={commands.onAlignLeft}
          active={states.isAlignLeft}
        >
          <Icon name="AlignLeft" />
        </MemoButton>
        <MemoButton
          tooltip="Align center"
          tooltipShortcut={['Shift', 'Mod', 'E']}
          onClick={commands.onAlignCenter}
          active={states.isAlignCenter}
        >
          <Icon name="AlignCenter" />
        </MemoButton>
        <MemoButton
          tooltip="Align right"
          tooltipShortcut={['Shift', 'Mod', 'R']}
          onClick={commands.onAlignRight}
          active={states.isAlignRight}
        >
          <Icon name="AlignRight" />
        </MemoButton>
        <MemoButton
          tooltip="Justify"
          tooltipShortcut={['Shift', 'Mod', 'J']}
          onClick={commands.onAlignJustify}
          active={states.isAlignJustify}
        >
          <Icon name="AlignJustify" />
        </MemoButton>
      </Toolbar.Wrapper>
    </BubbleMenu>
  )
}
